import { useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageContainer } from '@/components/PageContainer'
import { PageHeader } from '@/components/PageHeader'
import { FlashBanner } from '@/components/FlashBanner'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/lib/date'
import type { ProjectNav } from '@/types/project'
import type { ProjectMembershipSummary } from '@/types/project_membership'
import type { SharedData } from '@/types/shared'
import { PlusIcon } from 'lucide-react'

interface Props {
  project_nav: ProjectNav
  memberships: ProjectMembershipSummary[]
  current_user_id: number
  current_user_role: 'admin' | 'member' | null
}

export default function MembershipsIndex ({
  project_nav: project,
  memberships,
  current_user_id: currentUserId,
  current_user_role: currentUserRole
}: Props): React.ReactElement {
  const [leaveConfirm, setLeaveConfirm] = useState(false)
  const [removeTarget, setRemoveTarget] = useState<ProjectMembershipSummary | null>(null)
  const [processing, setProcessing] = useState(false)

  const { props } = usePage<SharedData>()
  const isAdmin = currentUserRole === 'admin'
  const isGuest = props.current_user?.account_type === 'guest'

  function handleRoleChange (membership: ProjectMembershipSummary, newRole: string): void {
    router.put(`/projects/${project.id}/memberships/${membership.id}`, {
      membership: { role: newRole }
    })
  }

  function handleLeave (): void {
    setProcessing(true)
    router.delete(`/projects/${project.id}/memberships/leave`, {
      onFinish: () => setProcessing(false)
    })
  }

  function handleRemove (): void {
    if (removeTarget == null) return
    setProcessing(true)
    router.delete(`/projects/${project.id}/memberships/${removeTarget.id}`, {
      onFinish: () => {
        setProcessing(false)
        setRemoveTarget(null)
      }
    })
  }

  return (
    <AppLayout project={project}>
      <PageContainer maxWidth='760px'>
        <PageHeader
          title='メンバー'
          backHref={`/projects/${project.id}`}
          backLabel={project.name}
        >
          {isAdmin && !isGuest && (
            <Button asChild>
              <Link href={`/projects/${project.id}/invitations/new`}>
                <PlusIcon width={14} height={14} />
                メンバーを招待する
              </Link>
            </Button>
          )}
        </PageHeader>

        <FlashBanner />

        <div className='bg-(--bp-surface) border border-(--bp-border) rounded-[10px]'>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>メールアドレス</th>
                  <th>権限</th>
                  <th>参加日</th>
                  <th style={{ textAlign: 'right' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {memberships.map((m) => {
                  const isSelf = m.user_id === currentUserId
                  return (
                    <tr key={m.id}>
                      <td>
                        <div className='flex items-center gap-[10px]'>
                          <div className='w-7 h-7 rounded-full bg-(--bp-accent) text-white text-[11px] font-bold flex items-center justify-center shrink-0'>
                            {m.email[0].toUpperCase()}
                          </div>
                          <div>
                            <div className='font-medium text-[13px]'>{m.email}</div>
                            {isSelf && (
                              <div className='text-[11px] text-(--bp-ink3)'>自分</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          className={
                            m.role === 'admin'
                              ? 'inline-block px-2 py-[2px] rounded text-[11px] font-semibold bg-(--bp-accent-pale) text-(--bp-accent)'
                              : 'inline-block px-2 py-[2px] rounded text-[11px] font-semibold bg-(--bp-surface2) text-(--bp-ink3)'
                          }
                        >
                          {m.role === 'admin' ? '管理者' : 'メンバー'}
                        </span>
                      </td>
                      <td className='text-(--bp-ink3) text-xs'>
                        {formatDate(m.joined_at)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {isSelf
                          ? (
                            <Button variant='destructive' onClick={() => setLeaveConfirm(true)}>
                              退出する
                            </Button>
                            )
                          : isAdmin
                            ? (
                              <div className='flex gap-1.5 justify-end'>
                                <select
                                  className='py-[5px] px-2 border border-(--bp-border) rounded-md text-xs text-(--bp-ink) bg-(--bp-surface)'
                                  value={m.role}
                                  onChange={(e) => handleRoleChange(m, e.target.value)}
                                >
                                  <option value='member'>メンバー</option>
                                  <option value='admin'>管理者</option>
                                </select>
                                <Button variant='destructive' onClick={() => setRemoveTarget(m)}>
                                  削除
                                </Button>
                              </div>
                              )
                            : null}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        <ConfirmDialog
          open={leaveConfirm}
          onClose={() => setLeaveConfirm(false)}
          onConfirm={handleLeave}
          title='プロジェクトから退出しますか？'
          description='退出するとこのプロジェクトへのアクセスができなくなります。'
          confirmLabel='退出する'
          processing={processing}
        />

        <ConfirmDialog
          open={removeTarget !== null}
          onClose={() => setRemoveTarget(null)}
          onConfirm={handleRemove}
          title='メンバーを削除しますか？'
          description={`${removeTarget?.email ?? ''} をプロジェクトから外します。`}
          confirmLabel='削除する'
          processing={processing}
        />
      </PageContainer>
    </AppLayout>
  )
}
